import { api } from './index';
import { isFeeCategoryGroupParent } from '../utils/feeCategoryCodeGrouping';
import {
  ApartmentPickerOption,
  toApartmentPickerOptions,
} from '../utils/apartmentSearch.util';

export interface PaymentRequest {
  serviceFeesIds: string[];
  /** Required when serviceFeesIds is empty (thu dư only). */
  apartmentId?: string;
  paymentMethod: 'CASH' | 'BANK_TRANSFER';
  totalAmount: number;
  amountReceived?: number;
  creditApplied?: number;
  projectBankAccountId?: string;
  paymentDate?: string;
  gatewayTransactionId?: string;
  notes?: string;
}

export interface UnpaidServiceFeesResponse {
  fees: UnpaidServiceFee[];
  totalReceivable: number;
}

export interface UnpaidServiceFee {
  id: string;
  apartmentId: string;
  feeCategoryId?: string | null;
  apartmentFeeCategoryId?: string | null;
  apartmentCode: string;
  categoryName?: string;
  feeCode?: string | null;
  licensePlate?: string | null;
  isParkingFee?: boolean;
  description: string;
  amount: number;
  displayOpeningDebt: number;
  openingDebt: number;
  incurredAmount: number;
  surchargeAmount?: number;
  vatAmount?: number | null;
  exemptionAmount?: number;
  totalReceivable: number;
  paidAmount: number;
  billingPeriod: string;
  invoiceType: string;
  status: string;
  dueDate?: string;
  fromDate?: string;
  startReading?: number;
  endReading?: number;
  usage?: number;
  quantity: number;
  notes?: string;
  isAutoGenerated?: boolean;
  createdAt?: string;
  meterCode?: string | null;
  feeMultiplier?: number | null;
  monthlyVehicleId?: string | null;
  utilityMeterId?: string | null;
  casualParkingFrom?: string;
  casualParkingTo?: string;
  machineAmount?: number | null;
  casualParkingVariances?: Array<{
    varianceType: 'DEDUCTION' | 'VOUCHER';
    counterpartyName: string;
    amount: number;
    notes?: string | null;
  }>;
}

export interface PaymentAllocationPreview {
  totalOwed: number;
  amountReceived: number;
  creditApplied: number;
  appliedAmount: number;
  overpaymentAmount: number;
  remainingDebt: number;
  allocations: Array<{
    serviceFeeId: string;
    amountAllocated: number;
    creditAllocated: number;
    cashAllocated: number;
    totalReceivable: number;
    remainingDebt: number;
    status: 'PAID' | 'PARTIAL';
  }>;
}

export interface PaymentContext {
  apartment: { id: string; code: string; name?: string | null; buildingId: string };
  surplusBalance: number;
  projectBankAccounts: Array<{ id: string; name: string; isDefault: boolean }>;
  defaultProjectBankAccountId: string | null;
  lockedProjectBankAccountId?: string | null;
  isBankAccountLocked?: boolean;
}

export interface PaymentResponse {
  id: string;
  billId?: string;
  transactionCode: string;
  totalAmount: number;
  paymentMethod: string;
  status: string;
  createdAt: string;
  paidAt?: string;
}

export interface ReconciliationRecord {
  id: string;
  billId?: string | null;
  receiptNumber?: string | null;
  voucherCode?: string | null;
  ledgerEntryId?: string | null;
  paymentGateway: string;
  referenceId?: string | null;
  bankReference?: string | null;
  merchantId?: string | null;
  accountNumber?: string | null;
  apartmentCode?: string | null;
  inferredApartmentCode?: string | null;
  transactionDate: string;
  description: string;
  amount: number;
  direction: 'CREDIT' | 'DEBIT' | 'OTHER';
  matchStatus: 'MATCHED' | 'UNMATCHED';
  currency?: string;
  paymentOrderCode?: string | null;
  callbackSource?: string | null;
  isSharedMotherAccount?: boolean;
}

export interface ReconciliationActionContext {
  transaction: {
    id: string;
    amount: number;
    description: string;
    transactionDate: string;
    accountNumber?: string | null;
    apartmentCode?: string | null;
    inferredApartmentCode?: string | null;
  };
  suggestedApartment: { id: string; code: string; name?: string | null } | null;
  projectBankAccounts: Array<{
    id: string;
    name: string;
    bankAccountNumber?: string | null;
    isDefault: boolean;
  }>;
  suggestedProjectBankAccountId: string | null;
  revenueCategories: Array<{ id: string; code: string; name: string }>;
}

export interface ApplySurplusDepositRequest {
  gatewayTransactionId: string;
  buildingId: string;
  apartmentId: string;
  revenueCategoryId: string;
  projectBankAccountId: string;
}

export interface ReconciliationQuery {
  buildingId: string;
  fromDate: string;
  toDate: string;
  paymentMethod?: string;
  direction?: string;
  matchStatus?: string;
  search?: string;
}

export interface Transaction {
  id: string;
  billId?: string;
  receiptNumber?: string;
  transactionCode: string;
  apartmentCode: string;
  staffId?: string;
  staffName?: string;
  transactionDate: string;
  createdAt?: string;
  paidAt?: string | null;
  paymentMethod: 'CASH' | 'BANK_TRANSFER' | 'VNPAY' | 'MOMO' | 'VIETQR' | 'SHINHAN' | 'ZALOPAY' | 'PAYOS';
  amount: number;
  appliedAmount?: number;
  amountReceived?: number;
  creditApplied?: number;
  overpaymentAmount?: number;
  fee: number;
  afterFee: number;
  status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'PAID' | 'CANCELLED';
  notes?: string;
  editable?: boolean;
  isCasualParkingSingleReceipt?: boolean;
}

export interface CasualParkingBillEditContext {
  invoiceId: string;
  buildingId: string;
  billingPeriod: string;
  machineAmount: number | null;
  actualAmount: number;
  varianceLines: Array<{
    varianceType: 'DEDUCTION' | 'VOUCHER';
    counterpartyName: string;
    amount: number;
    notes?: string | null;
  }>;
}

export interface TransactionBillEditContext {
  buildingId: string;
  projectBankAccountId: string | null;
  projectBankAccounts: Array<{
    id: string;
    name: string;
    bankAccountNumber: string | null;
    bankName: string | null;
    isDefault: boolean;
  }>;
  receiptNumber?: string | null;
  createdByName?: string;
  createdByEmail?: string | null;
  canAssignCasualParkingFeeType?: boolean;
  /** Đã gán T-VL001 trước đó nhưng cần đồng bộ lại Diễn giải / phân bổ. */
  needsCasualParkingReceiptSync?: boolean;
}

export interface TransactionBillCorrectionLog {
  id: string;
  activityType?: 'BILL_CORRECTION' | 'VOUCHER_SETTLEMENT';
  billId?: string | null;
  receiptNumber?: string | null;
  billCode?: string | null;
  apartmentCode: string;
  voucherAction?: string;
  voucherCounterparty?: string;
  voucherAmount?: number;
  billingPeriod?: string | null;
  previousPaymentMethod?: string | null;
  newPaymentMethod?: string | null;
  previousPaidAt?: string | null;
  newPaidAt?: string | null;
  previousAmountReceived?: number | null;
  newAmountReceived?: number | null;
  previousOverpaymentAmount?: number | null;
  newOverpaymentAmount?: number | null;
  changeSummary?: string | null;
  notes?: string | null;
  createdAt: string;
  createdByName: string;
}

export interface UpdateTransactionBillRequest {
  paymentMethod?: 'CASH' | 'BANK_TRANSFER';
  paymentDate?: string;
  amountReceived?: number;
  notes?: string;
  machineAmount?: number;
  varianceLines?: Array<{
    varianceType: 'DEDUCTION' | 'VOUCHER';
    counterpartyName: string;
    amount: number;
    notes?: string;
    contractApartmentId?: string;
  }>;
  /** Chênh lệch máy−thực thu đã thu trên phiếu khác — không phân bổ khấu trừ/voucher trên phiếu này */
  cashVarianceSettledOnOtherReceipt?: boolean;
  projectBankAccountId?: string;
  assignCasualParkingFeeType?: boolean;
}

export interface TransactionQuery {
  buildingId: string;
  fromDate: string;
  toDate: string;
  includeUnpaid?: boolean;
  /** Paid transactions: `paid` = ngày thu (paidAt), `created` = ngày tạo phiếu */
  dateFilter?: 'paid' | 'created';
  paymentMethod?: string;
  staffUserId?: string;
  /** PAID (default), CANCELLED, or ALL */
  billStatus?: string;
}

export const ONLINE_RESIDENT_COLLECTOR_ID = '__online_resident__';

export interface ReceivableFee {
  id: string;
  apartmentId: string;
  apartmentCode: string;
  apartmentName?: string | null;
  ownerName: string;
  receivableAmount: number;
  incurredAmount: number;
  paidAmount: number;
  remainingDebt: number;
  surplusBalance: number;
  amount: number;
}

export interface ReceivableReportQuery {
  buildingId: string;
  overdue?: boolean;
  apartmentGroupIds?: string[];
  apartmentSearch?: string;
  residentSearch?: string;
}

export interface ReceivablePeriodOverview {
  collectionPeriod: string;
  openingDebit: number;
  openingCredit: number;
  incurred: number;
  vat: number;
  deduction: number;
  totalIncurred: number;
  payment: number;
  preCollection: number;
  openingCreditHandoverImport?: number;
  preCollectionBillOverpayment?: number;
  preExpenditure: number;
  offset: number;
  closingDebit: number;
  closingCredit: number;
}

export interface WalletSurplusBreakdown {
  openingCreditHandoverImport: number;
  preCollectionOperational: number;
  preCollectionBillOverpayment: number;
  preCollectionOther: number;
  totalWalletCreditInPeriod: number;
}

export interface ReceivableDebtImportPreviewItem {
  apartmentCode: string;
  ownerName?: string;
  status: 'ready' | 'skipped';
  reason?: string;
  receivableAmount: number;
  paidAmount: number;
  remainingDebt: number;
  surplusBalance: number;
  replacedInvoiceCount: number;
}

export interface ReceivableDebtImportPreviewResult {
  formatLabel: string;
  billingPeriod: string;
  totalRows: number;
  readyCount: number;
  skippedCount: number;
  summary: {
    receivableAmount: number;
    paidAmount: number;
    remainingDebt: number;
    surplusBalance: number;
  };
  items: ReceivableDebtImportPreviewItem[];
  errors: string[];
}

export interface LegacyReceivableDebtCleanupPreviewItem {
  apartmentCode: string;
  billingPeriod: string;
  status: string;
  remainingDebt: number;
  paidAmount: number;
  blockedByPayment: boolean;
}

export interface LegacyReceivableDebtCleanupPreviewResult {
  buildingId: string;
  buildingName: string;
  invoiceCount: number;
  deletableInvoiceCount: number;
  blockedInvoiceCount: number;
  apartmentCount: number;
  billingPeriods: string[];
  summary: {
    receivableAmount: number;
    paidAmount: number;
    remainingDebt: number;
  };
  surplusAccountsToReset: number;
  surplusBalanceTotal: number;
  items: LegacyReceivableDebtCleanupPreviewItem[];
  warnings: string[];
}

export interface LegacyReceivableDebtCleanupResult {
  deletedInvoiceCount: number;
  resetSurplusAccountCount: number;
  skippedPaymentLinkedCount: number;
}

/** Synthetic column: ví / credit on VII only (backend `RECEIVABLE_SURPLUS_PAYMENT_COLUMN_ID`). */
export const RECEIVABLE_SURPLUS_PAYMENT_COLUMN_ID = '__receivable_surplus_thu_du__';

export const RECEIVABLE_SURPLUS_PAYMENT_COLUMN_NAME = 'Ví / credit (VII)';

export interface ReceivableFeeCategoryColumn {
  id: string;
  name: string;
  feeCode: string | null;
  period: string;
}

/** Excludes synthetic Ví / credit (X) bucket — ví theo II / VIII / X / XII, not a fee column. */
export function receivableFeeColumnsExcludingWalletSynthetic(
  columns: ReceivableFeeCategoryColumn[],
): ReceivableFeeCategoryColumn[] {
  return columns.filter(
    (column) =>
      column.id !== RECEIVABLE_SURPLUS_PAYMENT_COLUMN_ID &&
      !isFeeCategoryGroupParent(column.feeCode),
  );
}

/** Phát sinh (VI) columns — excludes Ví / credit (VII) synthetic column (not invoice debt). */
export function receivableFeeColumnsForIncurredSection(
  columns: ReceivableFeeCategoryColumn[],
): ReceivableFeeCategoryColumn[] {
  return receivableFeeColumnsExcludingWalletSynthetic(columns);
}

/** Số dư đầu kỳ & Thanh toán on Xem báo cáo detail grid. */
export function receivableFeeColumnsForDetailGrid(
  columns: ReceivableFeeCategoryColumn[],
): ReceivableFeeCategoryColumn[] {
  return receivableFeeColumnsExcludingWalletSynthetic(columns);
}

export interface ReceivableActivityCategoryColumn {
  id: string;
  code: string;
  name: string;
  kind: 'revenue' | 'expenditure';
}

export interface ReceivablePeriodReconciliationBillSample {
  receiptNumber: string | null;
  billId: string;
  transactionAmount: number;
  /** Phân bổ HĐ thuộc bộ TB kỳ (chưa gồm SHCĐ / thu dư / credit thừa). */
  paymentViiOnBill: number;
  cashAllocatedOnNoticeVii: number;
  creditAllocatedOnNoticeVii: number;
  creditApplied: number;
  gapViiMinusTransaction: number;
  cashOutsideVii: number;
  /** @deprecated Use cashOutsideVii */
  cashNotCountedInVii?: number;
  overpaymentAmount: number;
  paymentViiLedgerTotal: number;
  cashShcdToVii: number;
  extraBillCredit: number;
  cashToVii: number;
  creditToVii: number;
}

export interface ViiCashMatchBridge {
  statisticsPaymentVii: number;
  transactionReportCashTotal: number;
  gapViiMinusCash: number;
  sumCashAllocatedOnNoticeVii: number;
  sumCreditAllocatedOnNoticeVii: number;
  sumCreditAppliedOnBills: number;
  cashOnBillsWithoutFeeAllocations: number;
  cashExcessBeyondNoticeViiAllocations: number;
  sumCashPhongShcdTransaction: number;
  sumCashPhongShcdVii: number;
  viiLedgerExtras: number;
  cashOnSurplusOnlyBills: number;
  preCollectionSurplusViii: number;
}

export interface RemainingDebtOutsideNoticeInvoiceLine {
  invoiceId: string;
  feeCode: string | null;
  feeName: string | null;
  billingPeriod: string;
  status: string;
  closingDebt: number;
}

export interface RemainingDebtOutsideNoticeScopeRow {
  apartmentId: string;
  apartmentCode: string;
  remainingDebt: number;
  invoices: RemainingDebtOutsideNoticeInvoiceLine[];
}

export interface ApartmentLedgerFeeLine {
  feeCode: string | null;
  feeName: string;
  amount: number;
}

export interface ApartmentLedgerPaymentAllocationLine {
  feeCode: string | null;
  feeName: string | null;
  billingPeriod: string;
  amount: number;
}

export interface ApartmentLedgerPaymentBillLine {
  receiptNumber: string | null;
  paidAt: string;
  amountReceived: number;
  allocations: ApartmentLedgerPaymentAllocationLine[];
}

export interface AlgebraVsXiGapBreakdownRow {
  apartmentId: string;
  apartmentCode: string;
  openingDebit: number;
  totalIncurred: number;
  totalPayment: number;
  offset: number;
  ledgerNetBeforeXi: number;
  closingDebitXi: number;
  gapContribution: number;
  viXiAdjustment?: number;
  ledgerNetBeforeXiAdjustment?: number;
  incurredLines?: ApartmentLedgerFeeLine[];
  incurredAdjustmentLines?: ApartmentLedgerFeeLine[];
  paymentLines?: ApartmentLedgerFeeLine[];
  paymentBills?: ApartmentLedgerPaymentBillLine[];
}

export interface AlgebraVsXiGapSummary {
  totalGap: number;
  fromPerApartmentNonNegativeFloor: number;
  fromNoticeScopeDebtFloor: number;
}

export interface ReceivablePeriodReconciliation {
  transactionReportPaidAtRangeLabel?: string;
  transactionCashImpliedViiMinusOffset?: number;
  gapTransactionCashVsViiMinusOffset?: number;
  transactionReportCashTotal: number;
  statisticsPaymentVii: number;
  gapViiMinusCash: number;
  offsetX: number;
  sumCreditAppliedOnBills: number;
  cashOnBillsWithoutFeeAllocations: number;
  allocationsOutsideNoticeSet: number;
  receivableDebtTabRemaining: number;
  statisticsClosingDebitXi: number;
  debtVsStatisticsGap: number;
  buildingLevelAlgebraClosingDebit: number;
  perApartmentClosingDebitSum: number;
  algebraVsXiGap: number;
  algebraVsXiGapSummary?: AlgebraVsXiGapSummary;
  algebraVsXiGapBreakdown?: AlgebraVsXiGapBreakdownRow[];
  remainingDebtOnNoticeScopeInvoices: number;
  remainingDebtOutsideNoticeScope: number;
  remainingDebtOutsideNoticeScopeBreakdown?: RemainingDebtOutsideNoticeScopeRow[];
  viiCashMatch?: ViiCashMatchBridge;
  viiCashBillReconciliation?: ReceivablePeriodReconciliationBillSample[];
  sampleBillsIncreasingVii: ReceivablePeriodReconciliationBillSample[];
  sampleBillsIncreasingCash: ReceivablePeriodReconciliationBillSample[];
  walletSurplusBreakdown?: WalletSurplusBreakdown;
}

export interface ReceivablePeriodOverviewResponse {
  overview: ReceivablePeriodOverview;
  feeCategoryColumns: ReceivableFeeCategoryColumn[];
  revenueActivityColumns: ReceivableActivityCategoryColumn[];
  expenditureActivityColumns: ReceivableActivityCategoryColumn[];
  reconciliation?: ReceivablePeriodReconciliation;
  isFrozen: boolean;
  generatedAt: string;
}

export interface ReceivablePeriodDetailRow {
  apartmentId: string;
  apartmentCode: string;
  apartmentName: string | null;
  ownerName: string;
  openingDebit: number;
  openingCredit: number;
  openingByFee?: Record<string, number>;
  incurredByFee: Record<string, number>;
  totalIncurred: number;
  receivableThisMonth: number;
  preCollection: number;
  preExpenditure: number;
  paymentByFee: Record<string, number>;
  totalPayment: number;
  offset: number;
  closingDebit: number;
  closingCredit: number;
  /** VI added so XI matches notice-scope còn nợ (spread into incurredByFee). */
  viXiAdjustment?: number;
  viXiAdjustmentByFee?: Record<string, number>;
  collectionActivities: Record<string, number>;
  spendingActivities: Record<string, number>;
}

export interface ReceivablePeriodDetailResponse {
  collectionPeriod: string;
  isFrozen: boolean;
  generatedAt: string;
  feeCategoryColumns: ReceivableFeeCategoryColumn[];
  revenueActivityColumns: ReceivableActivityCategoryColumn[];
  expenditureActivityColumns: ReceivableActivityCategoryColumn[];
  totals: ReceivablePeriodDetailRow;
  items: ReceivablePeriodDetailRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ReceivableStatisticsQuery {
  buildingId: string;
  collectionPeriod: string;
  apartmentGroupIds?: string[];
  apartmentSearch?: string;
  feeCategoryId?: string;
  page?: number;
  limit?: number;
  refresh?: boolean;
}

export interface ReceivableCustomerLedgerLine {
  id: string;
  kind: 'opening' | 'entry' | 'incurred' | 'payment' | 'total' | 'closing';
  date: string | null;
  description: string;
  typeCode: string;
  debit: number;
  credit: number;
  serviceFeeInvoiceId?: string;
  billId?: string;
}

export interface ReceivableCustomerLedgerResponse {
  apartmentId: string;
  apartmentCode: string;
  ownerName: string;
  openingDebit: number;
  openingCredit: number;
  closingDebit: number;
  closingCredit: number;
  lines: ReceivableCustomerLedgerLine[];
  total: number;
}

export interface BillDetails {
  bill: {
    id: string;
    transactionCode: string;
    amount: number;
    paymentMethod: string;
    status: string;
    createdAt: string;
    paidAt: string;
    description: string;
    dueDate: string;
  };
  apartment: {
    id: string;
    code: string;
    ownerName: string;
    ownerPhone: string;
    ownerEmail: string;
    address: string;
    area: number;
  };
  building: {
    id: string;
    name: string;
    address: string;
  };
  serviceFees: Array<{
    id: string;
    categoryName: string;
    billingPeriod: string;
    invoiceType: string;
    amount: number;
    baseAmount: number;
    vatAmount: number;
    startReading?: number;
    endReading?: number;
    usage?: number;
    quantity: number;
    notes?: string;
    fromDate: string;
    dueDate: string;
  }>;
  paidBy: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface LegacyParkingShiftRevenueRow {
  id: string;
  shiftCode: string;
  staffName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  startedAtDisplay: string | null;
  endedAtDisplay: string | null;
  totalEntries: number;
  totalExits: number;
  totalOnSite: number;
  totalAmount: number;
  totalTurnCount: number | null;
  turnCountDetailJson: string | null;
  updatedAt: string;
}

export interface LegacyParkingGateTransactionRow {
  id: string;
  transactionCode: string;
  staffName: string | null;
  entryAt: string | null;
  exitAt: string | null;
  entryAtDisplay: string | null;
  exitAtDisplay: string | null;
  cardTypeName: string | null;
  amount: number | null;
  updatedAt: string;
}

export const paymentApi = {
  // Process payment for selected service fees
  processPayment: async (paymentData: PaymentRequest): Promise<PaymentResponse> => {
    const response = await api.post('/payments/process', paymentData);
    return response.data;
  },

  // Get transaction report
  getTransactionReport: async (query: TransactionQuery): Promise<Transaction[]> => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/payments/transactions/report?${params.toString()}`);
    return response.data;
  },

  getLegacyParkingShiftRevenues: async (
    buildingId: string,
    fromDate: string,
    toDate: string,
  ): Promise<LegacyParkingShiftRevenueRow[]> => {
    const params = new URLSearchParams({ buildingId, fromDate, toDate });
    const response = await api.get(`/payments/legacy-parking/shift-revenues?${params.toString()}`);
    return response.data;
  },

  getLegacyParkingGateTransactions: async (
    buildingId: string,
    fromDate: string,
    toDate: string,
  ): Promise<LegacyParkingGateTransactionRow[]> => {
    const params = new URLSearchParams({ buildingId, fromDate, toDate });
    const response = await api.get(`/payments/legacy-parking/gate-transactions?${params.toString()}`);
    return response.data;
  },

  canEditTransactions: async (): Promise<boolean> => {
    const response = await api.get<{ canEdit: boolean }>('/payments/transactions/can-edit');
    return Boolean(response.data?.canEdit);
  },

  canCancelTransactions: async (): Promise<boolean> => {
    const response = await api.get<{ canCancel: boolean }>('/payments/transactions/can-cancel');
    return Boolean(response.data?.canCancel);
  },

  getCasualParkingBillEditContext: async (billId: string): Promise<CasualParkingBillEditContext> => {
    const response = await api.get<CasualParkingBillEditContext>(
      `/payments/transactions/bills/${billId}/casual-parking-context`,
    );
    return response.data;
  },

  getTransactionBillEditContext: async (billId: string): Promise<TransactionBillEditContext> => {
    const response = await api.get<TransactionBillEditContext>(
      `/payments/transactions/bills/${billId}/edit-context`,
    );
    return response.data;
  },

  updateTransactionBill: async (
    billId: string,
    data: UpdateTransactionBillRequest,
  ): Promise<{
    id: string;
    paymentMethod: string;
    transactionDate: string;
    amountReceived?: number;
    overpaymentAmount?: number;
  }> => {
    const response = await api.patch(`/payments/transactions/bills/${billId}`, data);
    return response.data;
  },

  cancelTransactionBill: async (
    billId: string,
    notes?: string,
  ): Promise<{ billId: string; receiptNumber: string | null; status: string }> => {
    const response = await api.post(`/payments/transactions/bills/${billId}/cancel`, {
      notes: notes ?? '',
    });
    return response.data;
  },

  getTransactionCorrectionHistory: async (query: {
    buildingId: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
  }): Promise<TransactionBillCorrectionLog[]> => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    const response = await api.get(`/payments/transactions/correction-history?${params.toString()}`);
    return response.data;
  },

  syncShinhanVaHistory: async (data: {
    buildingId: string;
    fromDate: string;
    toDate: string;
    virtualAccountNumber?: string;
    dryRun?: boolean;
  }) => {
    const response = await api.post('/payment-gateways/shinhan/sync/va-history', data);
    return response.data;
  },

  getReconciliationReport: async (query: ReconciliationQuery): Promise<ReconciliationRecord[]> => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    const response = await api.get(`/payments/reconciliation?${params.toString()}`);
    return response.data;
  },

  getReconciliationActionContext: async (
    transactionId: string,
    buildingId: string,
  ): Promise<ReconciliationActionContext> => {
    const response = await api.get(
      `/payments/reconciliation/transactions/${transactionId}/context?buildingId=${buildingId}`,
    );
    return response.data;
  },

  applySurplusDeposit: async (data: ApplySurplusDepositRequest) => {
    const response = await api.post('/payments/reconciliation/surplus-deposit', data);
    return response.data;
  },

  // Get receivable fees report
  getReceivableReport: async (query: ReceivableReportQuery): Promise<ReceivableFee[]> => {
    const params = new URLSearchParams({ buildingId: query.buildingId });
    if (query.overdue) {
      params.append('overdue', 'true');
    }
    if (query.apartmentGroupIds?.length) {
      params.append('apartmentGroupIds', query.apartmentGroupIds.join(','));
    }
    if (query.apartmentSearch?.trim()) {
      params.append('apartmentSearch', query.apartmentSearch.trim());
    }
    if (query.residentSearch?.trim()) {
      params.append('residentSearch', query.residentSearch.trim());
    }

    const response = await api.get(`/payments/receivable?${params.toString()}`);
    return response.data;
  },

  downloadReceivableDebtImportSample: async (buildingId: string, billingPeriod: string): Promise<void> => {
    const response = await api.get('/payments/receivable/import-sample', {
      params: { buildingId, billingPeriod },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `mau-nhap-cong-no-${billingPeriod.replace('/', '-')}.xlsx`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  previewReceivableDebtImport: async (
    buildingId: string,
    billingPeriod: string,
    file: File,
  ): Promise<ReceivableDebtImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('billingPeriod', billingPeriod);
    const response = await api.post('/payments/receivable/import-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importReceivableDebt: async (
    buildingId: string,
    billingPeriod: string,
    file: File,
  ): Promise<{ importedCount: number; skippedCount: number; totalRows: number; errors: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('billingPeriod', billingPeriod);
    const response = await api.post('/payments/receivable/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  previewLegacyReceivableDebtImportCleanup: async (
    buildingId: string,
  ): Promise<LegacyReceivableDebtCleanupPreviewResult> => {
    const response = await api.get('/payments/receivable/legacy-import-cleanup-preview', {
      params: { buildingId },
    });
    return response.data;
  },

  clearLegacyReceivableDebtImport: async (
    buildingId: string,
  ): Promise<LegacyReceivableDebtCleanupResult> => {
    const response = await api.post('/payments/receivable/legacy-import-cleanup', { buildingId });
    return response.data;
  },

  // Search apartment by code or phone number (single match; Payment screen)
  searchApartment: async (buildingId: string, query: string) => {
    const response = await api.get(`/payments/apartments/search?buildingId=${buildingId}&query=${encodeURIComponent(query)}`);
    return response.data;
  },

  /** Apartment picker: list of matches (Hoạt động thu/chi, đối soát). */
  searchApartmentMatches: async (
    buildingId: string,
    query: string,
  ): Promise<ApartmentPickerOption[]> => {
    const response = await api.get(
      `/payments/apartments/search-matches?buildingId=${buildingId}&query=${encodeURIComponent(query)}`,
    );
    return toApartmentPickerOptions(response.data);
  },

  // Get unpaid service fees for apartment
  getUnpaidServiceFees: async (apartmentId: string): Promise<UnpaidServiceFeesResponse> => {
    const response = await api.get(`/payments/service-fees/${apartmentId}/unpaid`);
    const data = response.data;
    if (Array.isArray(data)) {
      return {
        fees: data,
        totalReceivable: data.reduce((sum: number, fee: UnpaidServiceFee) => sum + fee.amount, 0),
      };
    }
    return data;
  },

  previewPaymentAllocation: async (data: {
    serviceFeesIds: string[];
    amountReceived: number;
    creditApplied?: number;
  }): Promise<PaymentAllocationPreview> => {
    const response = await api.post('/payments/preview-allocation', data);
    return response.data;
  },

  previewPaymentReceipt: async (data: {
    apartmentId: string;
    serviceFeesIds: string[];
    amountReceived: number;
    creditApplied?: number;
    paymentMethod: 'CASH' | 'BANK_TRANSFER';
    paymentDate?: string;
    projectBankAccountId?: string;
  }): Promise<{ html: string }> => {
    const response = await api.post('/payments/preview-receipt', data);
    return response.data;
  },

  getPaymentContext: async (
    apartmentId: string,
    serviceFeeIds?: string[],
  ): Promise<PaymentContext> => {
    const params =
      serviceFeeIds && serviceFeeIds.length > 0
        ? { serviceFeeIds: serviceFeeIds.join(',') }
        : undefined;
    const response = await api.get(`/payments/apartments/${apartmentId}/payment-context`, {
      params,
    });
    return response.data;
  },

  // Get bill details with service fees
  getBillDetails: async (billId: string): Promise<BillDetails> => {
    const response = await api.get(`/bills/${billId}/details`);
    return response.data;
  },

  simulateVietqrPaymentByBill: async (billId: string): Promise<{ status: string; message?: string }> => {
    const response = await api.post('/payment-gateways/vietqr/test/simulate-by-bill', { billId });
    return response.data;
  },

  simulateShinhanPaymentByBill: async (billId: string): Promise<{ status: string; message?: string }> => {
    const response = await api.post('/payment-gateways/shinhan/test/simulate-by-bill', { billId });
    return response.data;
  },

  getReceivableStatisticsOverview: async (
    buildingId: string,
    collectionPeriod: string,
    refresh = false,
  ): Promise<ReceivablePeriodOverviewResponse> => {
    const params = new URLSearchParams({
      buildingId,
      collectionPeriod,
    });
    if (refresh) params.append('refresh', 'true');
    const response = await api.get(`/payments/receivable-statistics/overview?${params.toString()}`);
    return response.data;
  },

  getReceivableStatisticsDetail: async (
    query: ReceivableStatisticsQuery,
  ): Promise<ReceivablePeriodDetailResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      collectionPeriod: query.collectionPeriod,
    });
    if (query.apartmentGroupIds?.length) {
      params.append('apartmentGroupIds', query.apartmentGroupIds.join(','));
    }
    if (query.apartmentSearch?.trim()) {
      params.append('apartmentSearch', query.apartmentSearch.trim());
    }
    if (query.feeCategoryId) {
      params.append('feeCategoryId', query.feeCategoryId);
    }
    if (query.page) params.append('page', String(query.page));
    if (query.limit) params.append('limit', String(query.limit));
    if (query.refresh) params.append('refresh', 'true');
    const response = await api.get(`/payments/receivable-statistics/detail?${params.toString()}`);
    return response.data;
  },

  exportReceivableStatisticsDetail: async (
    query: Omit<ReceivableStatisticsQuery, 'page' | 'limit' | 'refresh'>,
  ): Promise<ReceivablePeriodDetailResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      collectionPeriod: query.collectionPeriod,
    });
    if (query.apartmentGroupIds?.length) {
      params.append('apartmentGroupIds', query.apartmentGroupIds.join(','));
    }
    if (query.apartmentSearch?.trim()) {
      params.append('apartmentSearch', query.apartmentSearch.trim());
    }
    if (query.feeCategoryId) {
      params.append('feeCategoryId', query.feeCategoryId);
    }
    const response = await api.get(`/payments/receivable-statistics/export?${params.toString()}`);
    return response.data;
  },

  getReceivableCustomerLedger: async (query: {
    buildingId: string;
    apartmentId?: string;
    apartmentCode?: string;
    periodFrom: string;
    periodTo: string;
    feeCategoryId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ReceivableCustomerLedgerResponse> => {
    const params = new URLSearchParams({
      buildingId: query.buildingId,
      periodFrom: query.periodFrom,
      periodTo: query.periodTo,
    });
    if (query.apartmentId) params.append('apartmentId', query.apartmentId);
    if (query.apartmentCode?.trim()) {
      params.append('apartmentCode', query.apartmentCode.trim());
    }
    if (query.feeCategoryId) params.append('feeCategoryId', query.feeCategoryId);
    if (query.dateFrom) params.append('dateFrom', query.dateFrom);
    if (query.dateTo) params.append('dateTo', query.dateTo);
    const response = await api.get(
      `/payments/receivable-statistics/customer-ledger?${params.toString()}`,
    );
    return response.data;
  },
}; 